/**
 * MEVIA Landing Page - Updated Version
 * Plataforma de Inteligencia Cultural Predictiva
 * Brand Colors: Azul Petróleo #1A4D5C, Dorado #C89846
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowRight, 
  CheckCircle, 
  Users, 
  TrendingUp, 
  Target,
  BarChart3,
  AlertTriangle,
  Zap,
  Heart,
  Building,
  UserCheck,
  RefreshCw,
  ChevronRight,
  Mail,
  Star,
  Menu,
  X,
  Globe,
  Send,
  Loader2,
  Linkedin,
  Instagram,
  Facebook,
  Shield,
  Activity
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { toast } from 'sonner';
import logger from '../utils/logger';


// Brand Colors
const COLORS = {
  primary: '#1A4D5C',
  primaryLight: '#2A6A7C',
  gold: '#C89846',
  goldLight: '#D4A85C',
  copper: '#8B5A3C',
  grayBlue: '#D4DCE0',
  darkGray: '#2C3E50',
  mediumGray: '#7F8C8D',
  lightGray: '#ECF0F1',
  white: '#FFFFFF'
};

// Social URLs
const LINKEDIN_URL = 'https://www.linkedin.com/company/meviatalent';
const INSTAGRAM_URL = 'https://www.instagram.com/meviatalent';
const FACEBOOK_URL = 'https://www.facebook.com/meviatalent';

// Logo URLs
const LOGO_HORIZONTAL = 'https://customer-assets.emergentagent.com/job_hr-tools-upgrade/artifacts/87hf4ifo_Mevia%20Hr%20Color.png';
const LOGO_VERTICAL = 'https://customer-assets.emergentagent.com/job_hr-tools-upgrade/artifacts/6p8kiw9t_Full_Color_V.png';

// Section Images - Updated with diverse/latin professionals
const IMAGES = {
  hero: 'https://images.unsplash.com/photo-1662595269110-d59d7c9183ae?w=1920&q=80', // Group of diverse professionals
  problem: 'https://images.unsplash.com/photo-1758876022104-368b0f3fb6c9?w=1920&q=80', // Workplace stress
  solution: 'https://images.unsplash.com/photo-1758691736483-5f600b509962?w=1920&q=80', // Analytics presentation
  indicators: 'https://images.unsplash.com/photo-1758876202980-0a28b744fb24?w=1920&q=80', // Data analytics discussion
  lifecycle: 'https://images.unsplash.com/photo-1758518731468-98e90ffd7430?w=1920&q=80', // Professional team success
  cta: 'https://images.unsplash.com/photo-1563457039413-0c39b8e8963d?w=1920&q=80' // Team meeting collaboration
};

// Translations - Updated
const translations = {
  es: {
    nav: {
      problem: 'El Problema',
      solution: 'Solución',
      indicators: 'Indicadores',
      plans: 'Planes',
      login: 'Iniciar Sesión'
    },
    hero: {
      title1: 'Alineación Cultural',
      title2: 'Basada en Datos',
      description: 'La plataforma de inteligencia que transforma decisiones de talento. Predicción, alineación y retención con análisis profundo de compatibilidad cultural.',
      cta1: 'Obtén tu Diagnóstico',
      cta2: 'Ver Planes'
    },
    problem: {
      badge: 'El Problema',
      title: 'Las Evaluaciones Tradicionales',
      titleHighlight: 'No Funcionan',
      stat: 'Rotación evitable',
      items: [
        { title: 'Candidatos Preparados', desc: 'Los tests tradicionales premian preparación, no autenticidad.' },
        { title: 'Cultura Aspiracional vs Real', desc: 'El discurso oficial diverge de la realidad diaria.' },
        { title: 'Desgaste Silencioso', desc: 'Compromiso perdido sin señales visibles.' },
        { title: 'Talento Desaprovechado', desc: 'Operación al 40% de capacidad potencial.' },
        { title: 'Rotación Anticipada', desc: 'Pérdida de conocimiento crítico del negocio.' }
      ]
    },
    solution: {
      badge: 'La Solución',
      title: 'MEVIA: Inteligencia Cultural',
      titleHighlight: 'Predictiva',
      statLabel: 'Evaluación holística',
      steps: [
        { title: 'Mide Prioridades Individuales', desc: 'Elecciones forzadas revelan patrones auténticos, no declaraciones.' },
        { title: 'Analiza Cultura Organizacional', desc: 'La cultura real se manifiesta en elecciones diarias.' },
        { title: 'Compara Compatibilidad', desc: 'Coincidencias indican sinergia; divergencias señalan riesgo.' },
        { title: 'Identifica Brechas Críticas', desc: 'Detección temprana antes de costos visibles.' }
      ],
      methodology: {
        title: 'Evaluación Completa',
        dimensions: { num: '10', label: 'Dimensiones Culturales', desc: 'Físico, Humano, Herramientas, Financiero, Natural, Cultural, Social, Conocimiento, Psicológico y Propósito' },
        types: { num: '9', label: 'Tipos de Personalidad', desc: 'Sistema Eneagrama Riso-Hudson con alas y niveles de desarrollo' },
        nom035: { num: '100%', label: 'NOM-035', desc: 'Normatividad mexicana completa para riesgos psicosociales' },
        pulse: { num: '∞', label: 'Pulso MEVIA', desc: 'Medición constante de la evolución cultural' }
      }
    },
    indicators: {
      badge: 'Resultados Medibles',
      title: 'Indicadores Predictivos',
      titleHighlight: 'Accionables',
      stats: [
        { num: '80-100', label: 'IRR Crítico', desc: 'Renuncia en 1-3 meses' },
        { num: '43%', label: 'Reducción', desc: 'Burnout prevenido' },
        { num: '50%', label: 'Tiempo', desc: 'Contratación ágil' }
      ],
      alerts: [
        { title: 'Riesgo Financiero', severity: 'Alta', desc: 'Requiere ajuste salarial inmediato.' },
        { title: 'Crisis de Propósito', severity: 'Alta', desc: 'Rediseño narrativo del rol necesario.' },
        { title: 'Talento Desperdiciado', severity: 'Media', desc: 'Movimiento lateral recomendado.' }
      ],
      compatibility: {
        title: 'Compatibilidad Bidireccional',
        subtitle: 'No solo evaluamos si puede hacer el trabajo, sino si quiere quedarse.',
        person: { title: 'Persona', items: ['¿Qué ofrece? Capacidades y fortalezas', '¿Qué necesita? Expectativas y búsqueda'] },
        company: { title: 'Empresa', items: ['¿Qué ofrece? Ambiente y beneficios', '¿Qué necesita? Requerimientos del rol'] }
      }
    },
    lifecycle: {
      title: 'Aplicación en Ciclo de Vida Completo',
      items: [
        { title: 'Reclutamiento', desc: 'Alineación predictiva' },
        { title: 'Onboarding', desc: 'Adaptación cultural' },
        { title: 'Desarrollo', desc: 'Crecimiento guiado' },
        { title: 'Retención', desc: 'Planes personalizados' },
        { title: 'Evaluaciones', desc: 'Gestión continua' },
        { title: 'Reestructuras', desc: 'Gestión del cambio' }
      ],
      caseStudy: {
        badge: 'Caso de Éxito',
        title: 'Empresa de Construcción',
        desc: '50+ evaluaciones: 8 empleados en riesgo ALTO identificados. 3 crisis de propósito resueltas.',
        quote: 'Por primera vez tenemos datos sobre quién está en riesgo ANTES de que renuncien'
      }
    },
    plans: {
      title: 'Planes de Inversión',
      subtitle: 'Elige el plan que mejor se adapte a tu organización',
      popular: 'Recomendada',
      perMonth: 'MXN/mes',
      noTax: 'No incluye IVA',
      cta: 'Comenzar',
      categories: {
        culture: {
          title: 'Cultura + Personalidad',
          icon: '🎯',
          description: 'Evaluación completa de dimensiones culturales y personalidad',
          includes: '10 Dimensiones + Eneagrama + MEVIA Score + Red Flags + Planes de Acción',
          tiers: [
            { name: 'STARTER', employees: '1-50 empleados', price: '$4,950' },
            { name: 'GROWTH', employees: '51-150 empleados', price: '$12,500' },
            { name: 'SCALE', employees: '151-300 empleados', price: '$24,000' }
          ]
        },
        compliance: {
          title: 'Cumplimiento NOM-035',
          icon: '📋',
          description: 'Cumplimiento total de la normatividad mexicana',
          includes: 'NOM-035 Completo + Pulso MEVIA + IPA + Planes de Acción NOM-035',
          tiers: [
            { name: 'COMPLIANCE', employees: '1-50 empleados', price: '$3,900' },
            { name: 'COMPLIANCE PRO', employees: '51-150 empleados', price: '$9,500' },
            { name: 'COMPLIANCE SCALE', employees: '151-300 empleados', price: '$18,000' }
          ]
        },
        complete: {
          title: 'Plataforma Completa',
          icon: '🚀',
          description: 'Todo incluido con análisis predictivo completo',
          includes: 'TODO (Cultura + NOM-035) + Dashboard integrado + Análisis predictivo completo',
          tiers: [
            { name: 'COMPLETE', employees: '1-50 empleados', price: '$7,500' },
            { name: 'COMPLETE PRO', employees: '51-150 empleados', price: '$18,500' },
            { name: 'COMPLETE SCALE', employees: '151-300 empleados', price: '$36,000' }
          ],
          popular: true
        }
      }
    },
    form: {
      title: 'Obtén tu Diagnóstico',
      subtitle: 'Completa el formulario y nos pondremos en contacto contigo',
      name: 'Nombre completo',
      email: 'Email corporativo',
      company: 'Empresa',
      employees: 'Número de empleados',
      employeesOptions: ['1-50', '51-150', '151-300', '300+'],
      submit: 'Enviar Solicitud',
      sending: 'Enviando...',
      success: '¡Solicitud enviada! Nos pondremos en contacto pronto.',
      error: 'Error al enviar. Intenta de nuevo.',
      privacyText: 'Al enviar este formulario aceptas nuestro',
      privacyLink: 'Aviso de Privacidad'
    },
    cta: {
      title: 'Comienza Tu Transformación Hoy',
      subtitle: 'Datos defendibles. Decisiones accionables. Resultados medibles.',
      contact: 'Contáctanos',
      demo: 'Obtén tu Diagnóstico'
    },
    footer: {
      login: 'Iniciar Sesión',
      contact: 'Contacto',
      privacy: 'Aviso de Privacidad',
      rights: 'Todos los derechos reservados.'
    }
  },
  en: {
    nav: {
      problem: 'The Problem',
      solution: 'Solution',
      indicators: 'Indicators',
      plans: 'Plans',
      login: 'Sign In'
    },
    hero: {
      title1: 'Cultural Alignment',
      title2: 'Data-Driven',
      description: 'The intelligence platform that transforms talent decisions. Prediction, alignment, and retention with deep cultural compatibility analysis.',
      cta1: 'Get Your Diagnosis',
      cta2: 'View Plans'
    },
    problem: {
      badge: 'The Problem',
      title: 'Traditional Assessments',
      titleHighlight: "Don't Work",
      stat: 'Avoidable turnover',
      items: [
        { title: 'Prepared Candidates', desc: 'Traditional tests reward preparation, not authenticity.' },
        { title: 'Aspirational vs Real Culture', desc: 'Official discourse diverges from daily reality.' },
        { title: 'Silent Burnout', desc: 'Lost commitment without visible signs.' },
        { title: 'Wasted Talent', desc: 'Operating at 40% of potential capacity.' },
        { title: 'Early Turnover', desc: 'Loss of critical business knowledge.' }
      ]
    },
    solution: {
      badge: 'The Solution',
      title: 'MEVIA: Cultural Intelligence',
      titleHighlight: 'Predictive',
      statLabel: 'Holistic assessment',
      steps: [
        { title: 'Measures Individual Priorities', desc: 'Forced choices reveal authentic patterns, not declarations.' },
        { title: 'Analyzes Organizational Culture', desc: 'Real culture manifests in daily choices.' },
        { title: 'Compares Compatibility', desc: 'Matches indicate synergy; divergences signal risk.' },
        { title: 'Identifies Critical Gaps', desc: 'Early detection before visible costs.' }
      ],
      methodology: {
        title: 'Complete Assessment',
        dimensions: { num: '10', label: 'Cultural Dimensions', desc: 'Physical, Human, Tools, Financial, Natural, Cultural, Social, Knowledge, Psychological and Purpose' },
        types: { num: '9', label: 'Personality Types', desc: 'Riso-Hudson Enneagram System with wings and development levels' },
        nom035: { num: '100%', label: 'NOM-035', desc: 'Complete Mexican regulation for psychosocial risks' },
        pulse: { num: '∞', label: 'MEVIA Pulse', desc: 'Constant measurement of cultural evolution' }
      }
    },
    indicators: {
      badge: 'Measurable Results',
      title: 'Predictive Indicators',
      titleHighlight: 'Actionable',
      stats: [
        { num: '80-100', label: 'Critical IRR', desc: 'Resignation in 1-3 months' },
        { num: '43%', label: 'Reduction', desc: 'Burnout prevented' },
        { num: '50%', label: 'Time', desc: 'Agile hiring' }
      ],
      alerts: [
        { title: 'Financial Risk', severity: 'High', desc: 'Requires immediate salary adjustment.' },
        { title: 'Purpose Crisis', severity: 'High', desc: 'Role narrative redesign needed.' },
        { title: 'Wasted Talent', severity: 'Medium', desc: 'Lateral movement recommended.' }
      ],
      compatibility: {
        title: 'Bidirectional Compatibility',
        subtitle: 'We not only assess if they can do the job, but if they want to stay.',
        person: { title: 'Person', items: ['What do they offer? Capabilities and strengths', 'What do they need? Expectations and search'] },
        company: { title: 'Company', items: ['What does it offer? Environment and benefits', 'What does it need? Role requirements'] }
      }
    },
    lifecycle: {
      title: 'Full Lifecycle Application',
      items: [
        { title: 'Recruitment', desc: 'Predictive alignment' },
        { title: 'Onboarding', desc: 'Cultural adaptation' },
        { title: 'Development', desc: 'Guided growth' },
        { title: 'Retention', desc: 'Personalized plans' },
        { title: 'Assessments', desc: 'Continuous management' },
        { title: 'Restructuring', desc: 'Change management' }
      ],
      caseStudy: {
        badge: 'Success Story',
        title: 'Construction Company',
        desc: '50+ assessments: 8 HIGH risk employees identified. 3 purpose crises resolved.',
        quote: 'For the first time we have data on who is at risk BEFORE they resign'
      }
    },
    plans: {
      title: 'Investment Plans',
      subtitle: 'Choose the plan that best fits your organization',
      popular: 'Recommended',
      perMonth: 'MXN/month',
      noTax: 'Tax not included',
      cta: 'Get Started',
      categories: {
        culture: {
          title: 'Culture + Personality',
          icon: '🎯',
          description: 'Complete assessment of cultural dimensions and personality',
          includes: '10 Dimensions + Enneagram + MEVIA Score + Red Flags + Action Plans',
          tiers: [
            { name: 'STARTER', employees: '1-50 employees', price: '$4,950' },
            { name: 'GROWTH', employees: '51-150 employees', price: '$12,500' },
            { name: 'SCALE', employees: '151-300 employees', price: '$24,000' }
          ]
        },
        compliance: {
          title: 'NOM-035 Compliance',
          icon: '📋',
          description: 'Full compliance with Mexican regulations',
          includes: 'Complete NOM-035 + MEVIA Pulse + IPA + NOM-035 Action Plans',
          tiers: [
            { name: 'COMPLIANCE', employees: '1-50 employees', price: '$3,900' },
            { name: 'COMPLIANCE PRO', employees: '51-150 employees', price: '$9,500' },
            { name: 'COMPLIANCE SCALE', employees: '151-300 employees', price: '$18,000' }
          ]
        },
        complete: {
          title: 'Complete Platform',
          icon: '🚀',
          description: 'All included with complete predictive analysis',
          includes: 'EVERYTHING (Culture + NOM-035) + Integrated Dashboard + Complete Predictive Analysis',
          tiers: [
            { name: 'COMPLETE', employees: '1-50 employees', price: '$7,500' },
            { name: 'COMPLETE PRO', employees: '51-150 employees', price: '$18,500' },
            { name: 'COMPLETE SCALE', employees: '151-300 employees', price: '$36,000' }
          ],
          popular: true
        }
      }
    },
    form: {
      title: 'Get Your Diagnosis',
      subtitle: 'Complete the form and we will contact you',
      name: 'Full name',
      email: 'Corporate email',
      company: 'Company',
      employees: 'Number of employees',
      employeesOptions: ['1-50', '51-150', '151-300', '300+'],
      submit: 'Send Request',
      sending: 'Sending...',
      success: 'Request sent! We will contact you soon.',
      error: 'Error sending. Please try again.',
      privacyText: 'By submitting this form you accept our',
      privacyLink: 'Privacy Notice'
    },
    cta: {
      title: 'Start Your Transformation Today',
      subtitle: 'Defensible data. Actionable decisions. Measurable results.',
      contact: 'Contact Us',
      demo: 'Get Your Diagnosis'
    },
    footer: {
      login: 'Sign In',
      contact: 'Contact',
      privacy: 'Privacy Notice',
      rights: 'All rights reserved.'
    }
  }
};

const getBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  return browserLang.startsWith('es') ? 'es' : 'en';
};

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isInView, setIsInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsInView(true);
      }
    }, { threshold: 0.1, ...options });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, []);

  return [ref, isInView];
};

const AnimatedSection = ({ children, className = '', delay = 0 }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-1000 ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(50px)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

const AnimatedCard = ({ children, className = '', delay = 0 }) => {
  const [ref, isInView] = useInView();
  
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ${className}`}
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0) scale(1)' : 'translateY(30px) scale(0.95)',
        transitionDelay: `${delay}ms`
      }}
    >
      {children}
    </div>
  );
};

// Lifecycle Cycle Graphic Component
const LifecycleCycleGraphic = ({ items, lang }) => {
  const cycleColors = [COLORS.primary, COLORS.gold, COLORS.copper, '#22c55e', '#3b82f6', '#8b5cf6'];
  
  return (
    <div className="relative w-full max-w-md mx-auto">
      <svg viewBox="0 0 400 400" className="w-full h-auto">
        {/* Outer circle */}
        <circle cx="200" cy="200" r="180" fill="none" stroke={COLORS.grayBlue} strokeWidth="2" />
        
        {/* Inner circle with gradient */}
        <defs>
          <linearGradient id="cycleGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={COLORS.primary} />
            <stop offset="100%" stopColor={COLORS.gold} />
          </linearGradient>
        </defs>
        <circle cx="200" cy="200" r="60" fill="url(#cycleGradient)" />
        <text x="200" y="195" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">MEVIA</text>
        <text x="200" y="212" textAnchor="middle" fill="white" fontSize="10">{lang === 'es' ? 'Ciclo' : 'Cycle'}</text>
        
        {/* Connecting lines and nodes */}
        {items.map((item, idx) => {
          const angle = (idx * 60 - 90) * (Math.PI / 180);
          const x = 200 + 140 * Math.cos(angle);
          const y = 200 + 140 * Math.sin(angle);
          const innerX = 200 + 70 * Math.cos(angle);
          const innerY = 200 + 70 * Math.sin(angle);
          
          return (
            <g key={idx}>
              {/* Line from center to node */}
              <line x1={innerX} y1={innerY} x2={x} y2={y} stroke={cycleColors[idx]} strokeWidth="2" strokeDasharray="4,4" />
              
              {/* Node circle */}
              <circle cx={x} cy={y} r="35" fill="white" stroke={cycleColors[idx]} strokeWidth="3" />
              
              {/* Number */}
              <text x={x} y={y - 5} textAnchor="middle" fill={cycleColors[idx]} fontSize="14" fontWeight="bold">{idx + 1}</text>
              
              {/* Title (abbreviated) */}
              <text x={x} y={y + 10} textAnchor="middle" fill={COLORS.darkGray} fontSize="8" fontWeight="500">
                {item.title.length > 10 ? item.title.substring(0, 10) + '...' : item.title}
              </text>
            </g>
          );
        })}
        
        {/* Arrows between nodes */}
        {items.map((_, idx) => {
          const angle1 = (idx * 60 - 90) * (Math.PI / 180);
          const angle2 = ((idx + 1) * 60 - 90) * (Math.PI / 180);
          const midAngle = ((idx * 60 + 30) - 90) * (Math.PI / 180);
          const arrowX = 200 + 155 * Math.cos(midAngle);
          const arrowY = 200 + 155 * Math.sin(midAngle);
          
          return (
            <path
              key={`arrow-${idx}`}
              d={`M ${arrowX - 5} ${arrowY - 5} L ${arrowX + 5} ${arrowY} L ${arrowX - 5} ${arrowY + 5}`}
              fill={COLORS.gold}
              transform={`rotate(${idx * 60 + 30}, ${arrowX}, ${arrowY})`}
            />
          );
        })}
      </svg>
      
      {/* Legend below */}
      <div className="grid grid-cols-2 gap-2 mt-4">
        {items.map((item, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm">
            <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cycleColors[idx] }} />
            <span style={{ color: COLORS.darkGray }}><strong>{idx + 1}.</strong> {item.title}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [lang, setLang] = useState(getBrowserLanguage());
  const [formData, setFormData] = useState({ name: '', email: '', company: '', employees: '' });
  const [formLoading, setFormLoading] = useState(false);
  const [selectedPlanCategory, setSelectedPlanCategory] = useState('complete');
  
  const t = translations[lang];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const response = await axios.post(`/contact/demo-request`, {
        name: formData.name,
        email: formData.email,
        company: formData.company,
        employees: formData.employees,
        language: lang
      });
      
      if (response.data.success) {
        toast.success(t.form.success);
        setFormData({ name: '', email: '', company: '', employees: '' });
      } else {
        toast.error(t.form.error);
      }
    } catch (error) {
      logger.error('Form submission error:', error);
      toast.error(t.form.error);
    } finally {
      setFormLoading(false);
    }
  };

  const toggleLanguage = () => {
    setLang(lang === 'es' ? 'en' : 'es');
  };

  const problemIcons = [Users, Building, Heart, Zap, RefreshCw];
  const stepIcons = [Target, Building, BarChart3, AlertTriangle];

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-2' : 'bg-transparent py-4'
      }`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <img 
                src={LOGO_HORIZONTAL} 
                alt="MEVIA" 
                className={`transition-all duration-500 ${scrolled ? 'h-8 md:h-10' : 'h-0 opacity-0'}`}
              />
            </div>

            <nav className="hidden md:flex items-center space-x-6">
              <button onClick={() => scrollToSection('problema')} className="text-gray-600 hover:text-[#1A4D5C] transition-colors text-sm font-medium">
                {t.nav.problem}
              </button>
              <button onClick={() => scrollToSection('solucion')} className="text-gray-600 hover:text-[#1A4D5C] transition-colors text-sm font-medium">
                {t.nav.solution}
              </button>
              <button onClick={() => scrollToSection('indicadores')} className="text-gray-600 hover:text-[#1A4D5C] transition-colors text-sm font-medium">
                {t.nav.indicators}
              </button>
              <button onClick={() => scrollToSection('planes')} className="text-gray-600 hover:text-[#1A4D5C] transition-colors text-sm font-medium">
                {t.nav.plans}
              </button>
              
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1 text-gray-500 hover:text-[#1A4D5C] transition-colors text-sm"
              >
                <Globe className="h-4 w-4" />
                {lang.toUpperCase()}
              </button>
              
              <Button 
                onClick={() => navigate('/auth')}
                style={{ backgroundColor: COLORS.primary }}
                className="hover:opacity-90 text-white px-6"
              >
                {t.nav.login}
              </Button>
            </nav>

            <div className="md:hidden flex items-center gap-3">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1 text-gray-500 text-sm"
              >
                <Globe className="h-4 w-4" />
                {lang.toUpperCase()}
              </button>
              <Button 
                onClick={() => navigate('/auth')}
                size="sm"
                style={{ backgroundColor: COLORS.primary }}
                className="text-white"
              >
                {t.nav.login}
              </Button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-gray-600"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden bg-white border-t mt-2 py-4 space-y-3 rounded-b-lg shadow-lg">
              <button onClick={() => scrollToSection('problema')} className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50">
                {t.nav.problem}
              </button>
              <button onClick={() => scrollToSection('solucion')} className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50">
                {t.nav.solution}
              </button>
              <button onClick={() => scrollToSection('indicadores')} className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50">
                {t.nav.indicators}
              </button>
              <button onClick={() => scrollToSection('planes')} className="block w-full text-left px-4 py-2 text-gray-600 hover:bg-gray-50">
                {t.nav.plans}
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section - NO "Data with Soul" badge */}
      <section className="relative min-h-screen flex items-center">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.hero} 
            alt="Team" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/95 to-white/70"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24 w-full">
          <div className="max-w-2xl">
            <div className={`transition-all duration-700 mb-8 ${scrolled ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'}`}>
              <img 
                src={LOGO_HORIZONTAL} 
                alt="MEVIA" 
                className="h-16 md:h-20 w-auto"
              />
            </div>
            
            <AnimatedSection delay={200}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight" style={{ color: COLORS.darkGray }}>
                {t.hero.title1}{' '}
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.gold})` }}>
                  {t.hero.title2}
                </span>
              </h1>
            </AnimatedSection>
            
            <AnimatedSection delay={400}>
              <p className="text-lg md:text-xl mb-8 leading-relaxed" style={{ color: COLORS.mediumGray }}>
                {t.hero.description}
              </p>
            </AnimatedSection>
            
            <AnimatedSection delay={600}>
              <div className="flex flex-col sm:flex-row items-start gap-4">
                <Button 
                  size="lg"
                  onClick={() => scrollToSection('demo')}
                  style={{ backgroundColor: COLORS.primary }}
                  className="text-white px-8 py-6 text-lg group hover:opacity-90"
                >
                  {t.hero.cta1}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button 
                  size="lg"
                  variant="outline"
                  onClick={() => scrollToSection('planes')}
                  className="px-8 py-6 text-lg"
                  style={{ borderColor: COLORS.grayBlue, color: COLORS.darkGray }}
                >
                  {t.hero.cta2}
                </Button>
              </div>
            </AnimatedSection>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-8 h-12 border-2 rounded-full flex items-start justify-center p-2" style={{ borderColor: COLORS.mediumGray }}>
            <div className="w-1.5 h-3 rounded-full animate-pulse" style={{ backgroundColor: COLORS.mediumGray }}></div>
          </div>
        </div>
      </section>

      {/* Problem Section - Image takes full space without frame */}
      <section id="problema" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection className="order-2 lg:order-1">
              <div className="relative h-[500px] overflow-hidden rounded-none lg:rounded-none">
                <img 
                  src={IMAGES.problem}
                  alt="Workplace stress"
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 right-0 text-white p-6 rounded-tl-2xl" style={{ backgroundColor: '#ef4444' }}>
                  <div className="text-3xl font-bold">67%</div>
                  <div className="text-sm opacity-90">{t.problem.stat}</div>
                </div>
              </div>
            </AnimatedSection>

            <div className="order-1 lg:order-2">
              <AnimatedSection>
                <Badge className="mb-4 bg-red-100 text-red-700 border-red-200">
                  {t.problem.badge}
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: COLORS.darkGray }}>
                  {t.problem.title}{' '}
                  <span className="text-red-500">{t.problem.titleHighlight}</span>
                </h2>
              </AnimatedSection>

              <div className="space-y-4">
                {t.problem.items.map((item, idx) => {
                  const Icon = problemIcons[idx];
                  const colors = ['#ef4444', '#f59e0b', '#8b5cf6', '#3b82f6', '#ec4899'];
                  return (
                    <AnimatedCard key={idx} delay={idx * 100}>
                      <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
                        <div 
                          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${colors[idx]}15` }}
                        >
                          <Icon className="h-5 w-5" style={{ color: colors[idx] }} />
                        </div>
                        <div>
                          <h3 className="font-semibold" style={{ color: COLORS.darkGray }}>{item.title}</h3>
                          <p className="text-sm" style={{ color: COLORS.mediumGray }}>{item.desc}</p>
                        </div>
                      </div>
                    </AnimatedCard>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section id="solucion" className="relative py-24 md:py-32" style={{ backgroundColor: COLORS.lightGray }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <AnimatedSection>
                <Badge className="mb-4 border" style={{ backgroundColor: `${COLORS.primary}10`, color: COLORS.primary, borderColor: `${COLORS.primary}20` }}>
                  {t.solution.badge}
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: COLORS.darkGray }}>
                  {t.solution.title}{' '}
                  <span className="text-transparent bg-clip-text" style={{ backgroundImage: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.gold})` }}>
                    {t.solution.titleHighlight}
                  </span>
                </h2>
              </AnimatedSection>

              <div className="space-y-6">
                {t.solution.steps.map((item, idx) => {
                  const Icon = stepIcons[idx];
                  return (
                    <AnimatedCard key={idx} delay={idx * 150}>
                      <div className="flex items-start gap-4 p-4 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow">
                        <div className="text-3xl font-bold" style={{ color: `${COLORS.primary}30` }}>0{idx + 1}</div>
                        <div>
                          <h3 className="font-semibold flex items-center gap-2" style={{ color: COLORS.darkGray }}>
                            <Icon className="h-4 w-4" style={{ color: COLORS.primary }} />
                            {item.title}
                          </h3>
                          <p className="text-sm mt-1" style={{ color: COLORS.mediumGray }}>{item.desc}</p>
                        </div>
                      </div>
                    </AnimatedCard>
                  );
                })}
              </div>
            </div>

            <AnimatedSection>
              <div className="relative">
                <img 
                  src={IMAGES.solution}
                  alt="Data analysis"
                  className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
                />
                <div className="absolute -bottom-6 -left-6 text-white p-6 rounded-2xl shadow-xl" style={{ backgroundImage: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.gold})` }}>
                  <div className="text-3xl font-bold">2 {lang === 'es' ? 'Pasos' : 'Steps'}</div>
                  <div className="text-sm opacity-90">{t.solution.statLabel}</div>
                </div>
              </div>
            </AnimatedSection>
          </div>

          {/* Updated Methodology Cards - 4 items */}
          <AnimatedSection className="mt-20">
            <div className="rounded-3xl p-8 md:p-12 text-white" style={{ backgroundImage: `linear-gradient(to right, ${COLORS.primary}, ${COLORS.copper})` }}>
              <h3 className="text-2xl md:text-3xl font-bold mb-8 text-center">{t.solution.methodology.title}</h3>
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  t.solution.methodology.dimensions,
                  t.solution.methodology.types,
                  t.solution.methodology.nom035,
                  t.solution.methodology.pulse
                ].map((item, idx) => {
                  const icons = [Target, Users, Shield, Activity];
                  const Icon = icons[idx];
                  return (
                    <AnimatedCard key={idx} delay={idx * 100}>
                      <div className="bg-white/10 backdrop-blur rounded-xl p-6 text-center hover:bg-white/20 transition-colors">
                        <Icon className="h-8 w-8 mx-auto mb-3" style={{ color: COLORS.gold }} />
                        <div className="text-3xl font-bold mb-2" style={{ color: COLORS.gold }}>{item.num}</div>
                        <div className="font-semibold mb-2">{item.label}</div>
                        <p className="text-xs text-white/70">{item.desc}</p>
                      </div>
                    </AnimatedCard>
                  );
                })}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Indicators Section */}
      <section id="indicadores" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <AnimatedSection>
              <div className="relative">
                <img 
                  src={IMAGES.indicators}
                  alt="HR Indicators"
                  className="rounded-3xl shadow-2xl w-full h-[500px] object-cover"
                />
              </div>
            </AnimatedSection>

            <div>
              <AnimatedSection>
                <Badge className="mb-4 border" style={{ backgroundColor: `${COLORS.gold}15`, color: COLORS.copper, borderColor: `${COLORS.gold}30` }}>
                  {t.indicators.badge}
                </Badge>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: COLORS.darkGray }}>
                  {t.indicators.title}{' '}
                  <span style={{ color: COLORS.gold }}>{t.indicators.titleHighlight}</span>
                </h2>
              </AnimatedSection>

              <div className="grid grid-cols-3 gap-4 mb-8">
                {t.indicators.stats.map((item, idx) => {
                  const colors = ['#ef4444', '#22c55e', COLORS.primary];
                  return (
                    <AnimatedCard key={idx} delay={idx * 100}>
                      <div className="bg-white rounded-xl p-4 shadow-lg text-center border-t-4" style={{ borderTopColor: colors[idx] }}>
                        <div className="text-2xl font-bold" style={{ color: colors[idx] }}>{item.num}</div>
                        <div className="font-semibold text-sm" style={{ color: COLORS.darkGray }}>{item.label}</div>
                        <p className="text-xs" style={{ color: COLORS.mediumGray }}>{item.desc}</p>
                      </div>
                    </AnimatedCard>
                  );
                })}
              </div>

              <div className="space-y-3">
                {t.indicators.alerts.map((item, idx) => {
                  const colors = ['#ef4444', '#f59e0b', '#3b82f6'];
                  return (
                    <AnimatedCard key={idx} delay={300 + idx * 100}>
                      <div className="flex items-center gap-4 p-4 rounded-lg border-l-4 bg-white shadow-sm" style={{ borderLeftColor: colors[idx] }}>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-semibold" style={{ color: COLORS.darkGray }}>{item.title}</span>
                            <Badge style={{ backgroundColor: `${colors[idx]}20`, color: colors[idx] }} className="text-xs">
                              {item.severity}
                            </Badge>
                          </div>
                          <p className="text-sm" style={{ color: COLORS.mediumGray }}>{item.desc}</p>
                        </div>
                      </div>
                    </AnimatedCard>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bidirectional Compatibility */}
          <AnimatedSection>
            <div className="bg-white rounded-3xl p-8 shadow-xl border" style={{ borderColor: COLORS.grayBlue }}>
              <h3 className="text-2xl font-bold mb-2 text-center" style={{ color: COLORS.darkGray }}>
                {t.indicators.compatibility.title}
              </h3>
              <p className="text-center mb-8" style={{ color: COLORS.mediumGray }}>
                {t.indicators.compatibility.subtitle}
              </p>
              <div className="grid md:grid-cols-2 gap-8">
                <AnimatedCard delay={100}>
                  <div className="rounded-xl p-6 hover:shadow-md transition-all" style={{ backgroundColor: `${COLORS.primary}08` }}>
                    <div className="flex items-center gap-3 mb-4">
                      <UserCheck className="h-8 w-8" style={{ color: COLORS.primary }} />
                      <h4 className="text-lg font-semibold">{t.indicators.compatibility.person.title}</h4>
                    </div>
                    <ul className="space-y-2 text-sm" style={{ color: COLORS.mediumGray }}>
                      {t.indicators.compatibility.person.items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4" style={{ color: COLORS.primary }} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedCard>
                <AnimatedCard delay={200}>
                  <div className="rounded-xl p-6 hover:shadow-md transition-all" style={{ backgroundColor: `${COLORS.gold}08` }}>
                    <div className="flex items-center gap-3 mb-4">
                      <Building className="h-8 w-8" style={{ color: COLORS.gold }} />
                      <h4 className="text-lg font-semibold">{t.indicators.compatibility.company.title}</h4>
                    </div>
                    <ul className="space-y-2 text-sm" style={{ color: COLORS.mediumGray }}>
                      {t.indicators.compatibility.company.items.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2">
                          <ChevronRight className="h-4 w-4" style={{ color: COLORS.gold }} />
                          {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </AnimatedCard>
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Lifecycle Section - Updated with Cycle Graphic */}
      <section className="relative py-24 md:py-32" style={{ backgroundColor: COLORS.lightGray }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.darkGray }}>
                {t.lifecycle.title}
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Cycle Graphic instead of grid of cards */}
            <AnimatedSection>
              <LifecycleCycleGraphic items={t.lifecycle.items} lang={lang} />
            </AnimatedSection>

            {/* Image + Case Study */}
            <AnimatedSection>
              <div className="relative">
                <img 
                  src={IMAGES.lifecycle}
                  alt="Successful team"
                  className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent rounded-3xl flex items-end">
                  <div className="p-6 text-white">
                    <Badge className="mb-3 text-white border-0" style={{ backgroundColor: COLORS.gold }}>{t.lifecycle.caseStudy.badge}</Badge>
                    <h3 className="text-xl font-bold mb-2">{t.lifecycle.caseStudy.title}</h3>
                    <p className="text-sm text-white/90 mb-3">
                      {t.lifecycle.caseStudy.desc}
                    </p>
                    <blockquote className="text-sm italic text-white/80 border-l-2 pl-3" style={{ borderLeftColor: COLORS.gold }}>
                      &ldquo;{t.lifecycle.caseStudy.quote}&rdquo;
                    </blockquote>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* NEW Pricing Section with 3 Categories */}
      <section id="planes" className="relative py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: COLORS.darkGray }}>
                {t.plans.title}
              </h2>
              <p style={{ color: COLORS.mediumGray }}>{t.plans.subtitle}</p>
            </div>
          </AnimatedSection>

          {/* Category Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {Object.entries(t.plans.categories).map(([key, cat]) => (
              <button
                key={key}
                onClick={() => setSelectedPlanCategory(key)}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                  selectedPlanCategory === key 
                    ? 'text-white shadow-lg scale-105' 
                    : 'bg-white border hover:shadow-md'
                }`}
                style={{ 
                  backgroundColor: selectedPlanCategory === key ? COLORS.primary : undefined,
                  borderColor: selectedPlanCategory === key ? undefined : COLORS.grayBlue,
                  color: selectedPlanCategory === key ? 'white' : COLORS.darkGray
                }}
              >
                <span className="text-xl">{cat.icon}</span>
                {cat.title}
                {cat.popular && (
                  <Badge className="bg-yellow-400 text-yellow-900 text-xs">{t.plans.popular}</Badge>
                )}
              </button>
            ))}
          </div>

          {/* Selected Category Plans */}
          <AnimatedSection>
            <div className="bg-white rounded-3xl p-8 shadow-xl border" style={{ borderColor: selectedPlanCategory === 'complete' ? COLORS.gold : COLORS.grayBlue, borderWidth: selectedPlanCategory === 'complete' ? 3 : 1 }}>
              <div className="text-center mb-8">
                <span className="text-4xl mb-4 block">{t.plans.categories[selectedPlanCategory].icon}</span>
                <h3 className="text-2xl font-bold mb-2" style={{ color: COLORS.darkGray }}>
                  {t.plans.categories[selectedPlanCategory].title}
                </h3>
                <p className="mb-4" style={{ color: COLORS.mediumGray }}>
                  {t.plans.categories[selectedPlanCategory].description}
                </p>
                <div className="inline-block px-4 py-2 rounded-lg" style={{ backgroundColor: `${COLORS.primary}10` }}>
                  <p className="text-sm font-medium" style={{ color: COLORS.primary }}>
                    {lang === 'es' ? 'Incluye:' : 'Includes:'} {t.plans.categories[selectedPlanCategory].includes}
                  </p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-6">
                {t.plans.categories[selectedPlanCategory].tiers.map((tier, idx) => (
                  <AnimatedCard key={idx} delay={idx * 100}>
                    <div className={`p-6 rounded-2xl border-2 text-center transition-all hover:shadow-lg ${idx === 1 ? 'border-[#C89846] bg-gradient-to-b from-amber-50 to-white' : 'border-gray-200'}`}>
                      <h4 className="font-bold text-lg mb-1" style={{ color: COLORS.darkGray }}>{tier.name}</h4>
                      <p className="text-sm mb-4" style={{ color: COLORS.mediumGray }}>{tier.employees}</p>
                      <div className="mb-6">
                        <span className="text-4xl font-bold" style={{ color: idx === 1 ? COLORS.gold : COLORS.darkGray }}>{tier.price}</span>
                        <span className="text-sm" style={{ color: COLORS.mediumGray }}> {t.plans.perMonth}</span>
                        <p className="text-xs mt-1" style={{ color: COLORS.mediumGray }}>{t.plans.noTax}</p>
                      </div>
                      <Button 
                        className="w-full text-white"
                        style={{ backgroundColor: idx === 1 ? COLORS.gold : COLORS.primary }}
                        onClick={() => scrollToSection('demo')}
                      >
                        {t.plans.cta}
                      </Button>
                    </div>
                  </AnimatedCard>
                ))}
              </div>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Demo Form Section */}
      <section id="demo" className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.cta} 
            alt="Team" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to right, ${COLORS.primary}f0, ${COLORS.copper}f0)` }}></div>
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <AnimatedSection>
              <img src={LOGO_HORIZONTAL} alt="MEVIA" className="h-12 mb-6 brightness-0 invert" />
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                {t.cta.title}
              </h2>
              <p className="text-xl text-white/80 mb-8">
                {t.cta.subtitle}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg"
                  onClick={() => window.location.href = 'mailto:hola@mevia.mx'}
                  className="bg-white px-6 group hover:bg-gray-100"
                  style={{ color: COLORS.primary }}
                >
                  <Mail className="mr-2 h-5 w-5" />
                  {t.cta.contact}
                </Button>
                <div className="flex gap-2">
                  <a
                    href={LINKEDIN_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-11 px-4 border-2 border-white text-white hover:bg-white/20 rounded-md transition-colors"
                  >
                    <Linkedin className="h-5 w-5" />
                  </a>
                  <a
                    href={INSTAGRAM_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-11 px-4 border-2 border-white text-white hover:bg-white/20 rounded-md transition-colors"
                  >
                    <Instagram className="h-5 w-5" />
                  </a>
                  <a
                    href={FACEBOOK_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center h-11 px-4 border-2 border-white text-white hover:bg-white/20 rounded-md transition-colors"
                  >
                    <Facebook className="h-5 w-5" />
                  </a>
                </div>
              </div>
              <p className="mt-8 text-white/60 text-sm">
                hola@mevia.mx
              </p>
            </AnimatedSection>

            <AnimatedSection delay={200}>
              <Card className="shadow-2xl">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-bold mb-2" style={{ color: COLORS.darkGray }}>
                    {t.form.title}
                  </h3>
                  <p className="mb-6" style={{ color: COLORS.mediumGray }}>
                    {t.form.subtitle}
                  </p>
                  
                  <form onSubmit={handleFormSubmit} className="space-y-4">
                    <div>
                      <Input
                        placeholder={t.form.name}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="border-gray-300 focus:border-[#1A4D5C] focus:ring-[#1A4D5C]"
                      />
                    </div>
                    <div>
                      <Input
                        type="email"
                        placeholder={t.form.email}
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="border-gray-300 focus:border-[#1A4D5C] focus:ring-[#1A4D5C]"
                      />
                    </div>
                    <div>
                      <Input
                        placeholder={t.form.company}
                        value={formData.company}
                        onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                        required
                        className="border-gray-300 focus:border-[#1A4D5C] focus:ring-[#1A4D5C]"
                      />
                    </div>
                    <div>
                      <select
                        value={formData.employees}
                        onChange={(e) => setFormData({ ...formData, employees: e.target.value })}
                        required
                        className="w-full h-10 px-3 rounded-md border border-gray-300 text-sm focus:outline-none focus:ring-2 focus:ring-[#1A4D5C] focus:border-[#1A4D5C]"
                        style={{ color: formData.employees ? COLORS.darkGray : COLORS.mediumGray }}
                      >
                        <option value="">{t.form.employees}</option>
                        {t.form.employeesOptions.map((opt, idx) => (
                          <option key={idx} value={opt}>{opt}</option>
                        ))}
                      </select>
                    </div>
                    <Button 
                      type="submit"
                      className="w-full text-white"
                      style={{ backgroundColor: COLORS.primary }}
                      disabled={formLoading}
                    >
                      {formLoading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          {t.form.sending}
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-4 w-4" />
                          {t.form.submit}
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-center mt-3" style={{ color: COLORS.mediumGray }}>
                      {t.form.privacyText}{' '}
                      <button 
                        type="button"
                        onClick={() => navigate('/privacidad')}
                        className="underline hover:opacity-80 transition-opacity"
                        style={{ color: COLORS.primary }}
                        data-testid="form-privacy-link"
                      >
                        {t.form.privacyLink}
                      </button>
                    </p>
                  </form>
                </CardContent>
              </Card>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Footer with Social Links */}
      <footer className="py-12" style={{ backgroundColor: COLORS.darkGray }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <img src={LOGO_HORIZONTAL} alt="MEVIA" className="h-8 brightness-0 invert" />
            </div>
            <div className="flex items-center gap-6">
              <button
                onClick={toggleLanguage}
                className="flex items-center gap-1 text-sm transition-colors hover:text-white"
                style={{ color: COLORS.mediumGray }}
              >
                <Globe className="h-4 w-4" />
                {lang === 'es' ? 'English' : 'Español'}
              </button>
              <button onClick={() => navigate('/auth')} className="text-sm transition-colors hover:text-white" style={{ color: COLORS.mediumGray }}>
                {t.footer.login}
              </button>
              <a href="mailto:hola@mevia.mx" className="text-sm transition-colors hover:text-white" style={{ color: COLORS.mediumGray }}>
                {t.footer.contact}
              </a>
              <button onClick={() => navigate('/privacidad')} className="text-sm transition-colors hover:text-white" style={{ color: COLORS.mediumGray }} data-testid="footer-privacy-link">
                {t.footer.privacy}
              </button>
              <div className="flex items-center gap-3">
                <a 
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-white"
                  style={{ color: COLORS.mediumGray }}
                >
                  <Linkedin className="h-5 w-5" />
                </a>
                <a 
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-white"
                  style={{ color: COLORS.mediumGray }}
                >
                  <Instagram className="h-5 w-5" />
                </a>
                <a 
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-colors hover:text-white"
                  style={{ color: COLORS.mediumGray }}
                >
                  <Facebook className="h-5 w-5" />
                </a>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t text-center text-sm" style={{ borderTopColor: '#3d5a6e', color: COLORS.mediumGray }}>
            © {new Date().getFullYear()} MEVIA. {t.footer.rights}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
